/**
 * SkillCheckSystem.ts — Deterministic stat threshold checks.
 *
 * No dice rolls. A skill check passes if playerValue >= threshold.
 * The margin (playerValue - threshold) is used for UI feedback
 * (e.g., "barely passed" vs "easily passed").
 */

import type { StatName, StoryChoice, SkillCheck } from '@game/data/types';
import type { GameState } from '@core/engine/GameState';
import type { SkillSystem } from './SkillSystem';

// ─── Result Types ─────────────────────────────────────────────

export interface SkillCheckResult {
  stat: StatName;
  playerValue: number;
  threshold: number;
  passed: boolean;
  /** Positive = passed by this much, negative = failed by this much. */
  margin: number;
}

export interface SkillCheckPreview {
  stat: StatName;
  playerValue: number;
  threshold: number;
  willPass: boolean;
}

// ─── Margin Labels ────────────────────────────────────────────

export type MarginLabel =
  | 'critical_success'   // margin >= 6
  | 'easy_pass'          // margin >= 3
  | 'narrow_pass'        // margin >= 0
  | 'narrow_fail'        // margin >= -3
  | 'clear_fail'         // margin >= -6
  | 'critical_fail';     // margin < -6

// ─── SkillCheckSystem ─────────────────────────────────────────

export class SkillCheckSystem {
  private skillSystem: SkillSystem | null = null;

  /** Wire a SkillSystem so stat checks include skill tree bonuses. */
  setSkillSystem(skillSystem: SkillSystem): void {
    this.skillSystem = skillSystem;
  }

  /**
   * Evaluate a stat check against the current game state.
   * Returns the full result with pass/fail and margin.
   */
  evaluateCheck(stat: StatName, difficulty: number, state: GameState): SkillCheckResult {
    let playerValue = state.getEffectiveStat(stat);
    if (this.skillSystem) {
      playerValue += this.skillSystem.getStatCheckBonus(stat, state);
    }
    const passed = playerValue >= difficulty;
    const margin = playerValue - difficulty;

    return {
      stat,
      playerValue,
      threshold: difficulty,
      passed,
      margin,
    };
  }

  /**
   * Get a preview of a skill check (shown before the player confirms).
   * Identical to evaluateCheck but with a simpler return type.
   */
  getCheckPreview(stat: StatName, difficulty: number, state: GameState): SkillCheckPreview {
    let playerValue = state.getEffectiveStat(stat);
    if (this.skillSystem) {
      playerValue += this.skillSystem.getStatCheckBonus(stat, state);
    }
    return {
      stat,
      playerValue,
      threshold: difficulty,
      willPass: playerValue >= difficulty,
    };
  }

  /**
   * Check if a choice's skill check would pass.
   * Returns true if no skill check exists (choice is always available).
   */
  wouldPassCheck(choice: StoryChoice, state: GameState): boolean {
    if (!choice.skillCheck) return true;
    const result = this.evaluateCheck(
      choice.skillCheck.stat,
      choice.skillCheck.difficulty,
      state,
    );
    return result.passed;
  }

  /**
   * Resolve a skill check on a choice and return the target node ID.
   * Returns successNode if passed, failureNode if failed.
   */
  resolveCheck(skillCheck: SkillCheck, state: GameState): { result: SkillCheckResult; nextNodeId: string } {
    const result = this.evaluateCheck(skillCheck.stat, skillCheck.difficulty, state);
    const nextNodeId = result.passed ? skillCheck.successNode : skillCheck.failureNode;
    return { result, nextNodeId };
  }

  /**
   * Get a human-readable label for the margin of success/failure.
   */
  getMarginLabel(margin: number): MarginLabel {
    if (margin >= 6) return 'critical_success';
    if (margin >= 3) return 'easy_pass';
    if (margin >= 0) return 'narrow_pass';
    if (margin >= -3) return 'narrow_fail';
    if (margin >= -6) return 'clear_fail';
    return 'critical_fail';
  }
}
